// src/Payroll_Management/PayRoll_Report/EmployeeAllReportPrint.jsx
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://nissi-india.com"; // change if needed

function toDmy(dateStr) {
  const s = (dateStr ?? "").toString().trim();
  const m1 = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m1) return s;
  const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m2) return `${m2[3]}/${m2[2]}/${m2[1]}`;
  return s; // if already in dd/MM/yyyy or empty
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const EmployeeAllReportPrint = () => {
  const finYearId = localStorage.getItem("finYearId") || "";
  const headerBranchId = localStorage.getItem("branchId") || "";
  const headerBranchType = localStorage.getItem("branchType") || "HO"; // "HO" or others

  const q = useQuery();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    companyName: "Company",
    address: "",
    phone: "",
  });

  const filters = useMemo(() => {
    return {
      site: q.get("site") || "Select",
      deptId: q.get("deptId") || "Select",
      desigId: q.get("desigId") || "Select",
      empCode: (q.get("empCode") || "").trim(),
      empName: (q.get("empName") || "").trim(),
      payMode: q.get("payMode") || "Select",
      pfNonPf: q.get("pfNonPf") || "Select",
      fromDate: toDmy(q.get("fromDate") || ""),
      toDate: toDmy(q.get("toDate") || ""),
    };
  }, [q]);

  const headerAxios = () => ({
    headers: {
      "x-finyear-id": Number(finYearId) || "",
      "x-branch-id": headerBranchId ? Number(headerBranchId) : "",
      "x-branch-type": headerBranchType || "",
    },
  });

  // Fetch meta header (optional). If you have a company details API, plug it here.
  // For now, we just set a placeholder header similar to your screenshot.
  const loadHeaderMeta = async () => {
    // If you later create: GET /api/company/meta
    // then call it here and set companyName/address/phone/logo
    setMeta({
      companyName: "Employee Master Report",
      address: "",
      phone: "",
    });
  };

  const fetchAllRows = async () => {
    if (!String(finYearId).trim()) {
      // Still print page loads, but without data
      setRows([]);
      setLoading(false);
      return;
    }

    // Node limit: pageSize max 200 -> loop all pages
    const pageSize = 200;
    let page = 1;
    let all = [];
    let totalPages = 1;

    while (page <= totalPages) {
      const res = await axios.get(`${BASE_URL}/api/employee-report`, {
        params: {
          finYearId: Number(finYearId),
          page,
          pageSize,
          ...filters,
        },
        ...headerAxios(),
      });

      if (!res?.data?.success) {
        throw new Error(res?.data?.message || "Failed to load report");
      }

      const data = res.data;
      totalPages = Number(data.totalPages || 1);
      all = all.concat(data.rows || []);
      page += 1;
    }

    return all;
  };

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        setLoading(true);
        await loadHeaderMeta();
        const all = await fetchAllRows();
        if (!alive) return;

        setRows(all || []);
        setLoading(false);

        // print after a short tick so DOM paints
        setTimeout(() => {
          window.print();
        }, 300);
      } catch (e) {
        if (!alive) return;
        setRows([]);
        setLoading(false);
        // still allow printing empty page if needed
        console.error(e);
      }
    };

    run();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="print-root">
      {/* Print CSS */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .print-root {
          padding: 14px;
          font-family: Arial, Helvetica, sans-serif;
          color: #000;
        }
        .sheet {
          border: 1px solid #000;
          padding: 10px;
        }
        .headerBox {
          display: grid;
          grid-template-columns: 140px 1fr;
          gap: 12px;
          align-items: center;
          border: 1px solid #000;
          padding: 10px;
          margin: 0 auto 10px auto;
          max-width: 1050px;
        }
        .logoBox {
          width: 120px;
          height: 80px;
          border: 1px solid #000;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight:700;
          font-size:12px;
        }
        .headText {
          text-align: center;
          line-height: 1.3;
        }
        .headText .name {
          font-size: 18px;
          font-weight: 700;
        }
        .headText .sub {
          font-size: 13px;
          font-weight: 700;
          margin-top: 2px;
        }
        .headText .small {
          font-size: 12px;
          margin-top: 2px;
        }
        .titleBar {
          border-top: 1px solid #000;
          border-bottom: 1px solid #000;
          padding: 8px 0;
          margin: 10px 0 8px 0;
          text-align: center;
          font-weight: 700;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        th, td {
          border: 1px solid #000;
          padding: 6px 6px;
          vertical-align: middle;
        }
        th {
          font-weight: 700;
          text-align: center;
        }
        td {
          text-align: left;
        }
        td.c, th.c { text-align: center; }
        td.r, th.r { text-align: right; }
        .maxWidth {
          max-width: 1050px;
          margin: 0 auto;
        }
      `}</style>

      {/* Controls (no-print) */}
      <div className="no-print maxWidth" style={{ display: "flex", gap: 10, marginBottom: 10 }}>
        <button onClick={() => window.print()} style={{ padding: "8px 14px", fontWeight: 700 }}>
          Print
        </button>
        <button onClick={() => window.close()} style={{ padding: "8px 14px", fontWeight: 700 }}>
          Close
        </button>
      </div>

      <div className="sheet maxWidth">
        {/* Header like screenshot */}
        <div className="headerBox">
          <div className="logoBox">LOGO</div>
          <div className="headText">
            <div className="name">{meta.companyName || "Company Name"}</div>
            <div className="sub">EMPLOYEE MASTER REPORT</div>
            {meta.address ? <div className="small">{meta.address}</div> : null}
            {meta.phone ? <div className="small">Phone: {meta.phone}</div> : null}
          </div>
        </div>

        <div className="titleBar">EMPLOYEE MASTER REPORT</div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 20 }}>Loading...</div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: "center", padding: 20 }}>No Records Found</div>
        ) : (
          <div style={{ overflow: "hidden" }}>
            <table>
              <thead>
                <tr>
                  <th className="c">S.No</th>
                  <th className="c">Employee Code</th>
                  <th>Employee Name</th>
                  <th className="c">Designation</th>
                  <th className="c">PF No.</th>
                  <th className="c">A/C No.</th>
                  <th className="c">DOB</th>
                  <th className="c">Qualification</th>
                  <th className="c">DOJ</th>
                  <th className="r">Gross Salary</th>
                  <th className="r">Batta/Day</th>
                  <th className="r">Batta/Month</th>
                  <th className="r">Grand Total</th>
                  <th className="r">Special HRA</th>
                  <th className="c">PF</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx}>
                    <td className="c">{idx + 1}</td>
                    <td className="c">{r.fld_Employeecode ?? r.fld_employeecode ?? ""}</td>
                    <td>{r.fld_Employee ?? r.fld_employee ?? ""}</td>
                    <td className="c">{r.fld_designationname ?? ""}</td>
                    <td className="c">{r.fld_pfno ?? ""}</td>
                    <td className="c">{r.fld_accountno ?? ""}</td>
                    <td className="c">{r.DOB ?? r.dob ?? ""}</td>
                    <td className="c">{r.fld_qualificationname ?? ""}</td>
                    <td className="c">{r.DOJ ?? r.doj ?? ""}</td>
                    <td className="r">{r.fld_grosssalary ?? ""}</td>
                    <td className="r">{r.fld_dailybata ?? ""}</td>
                    <td className="r">{r.battaMonth ?? r.fld_battamonth ?? ""}</td>
                    <td className="r">{r.grandTot ?? r.grandtot ?? ""}</td>
                    <td className="r">{r.fld_SpecialHRA ?? r.fld_specialhra ?? ""}</td>
                    <td className="c">{r.fld_pf ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeAllReportPrint;
